import { useState } from "react";
import { useParams } from "react-router-dom";
import { Flex, Text, Badge, Link } from "@radix-ui/themes";
import { ConfigurationViewGuard } from "./parts/ConfigurationViewGuard";
import { useSimulatorConfiguration } from "./useSimulatorConfiguration";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/v2/components/dataTabs/tabs";
import { SnippetsTab } from "./resources/tabs/SnippetsTab";
import { ArticlesTab } from "./resources/tabs/ArticlesTab";
import { WikiTab } from "./resources/tabs/WikiTab";
import { AiFolderIcon } from "@/v2/assets/icons/AiFolderIcon";
import { Ticket03Icon } from "@/v2/assets/icons/Ticket03Icon";
import { File02Icon } from "@/v2/assets/icons/File02Icon";

type ResourceTab = "snippets" | "articles" | "wiki" | "ticketing";

export function ResourcesConfiguration() {
  const { simulatorId } = useParams();
  const { simulator } = useSimulatorConfiguration(simulatorId);
  const [activeTab, setActiveTab] = useState<ResourceTab>("snippets");

  const draftReleaseId = simulator?.draftReleaseId ?? undefined;
  const publishedReleaseId = simulator?.publishedReleaseId ?? undefined;

  return (
    <ConfigurationViewGuard view="resources">
      <Flex direction="column" gap="4" width="100%">
        <Tabs
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as ResourceTab)}
        >
          <TabsList>
            <TabsTrigger value="snippets">
              <AiFolderIcon fill="amber" size={14} />
              Snippets
            </TabsTrigger>
            <TabsTrigger value="articles">
              <File02Icon fill="sky" size={14} />
              Articles
            </TabsTrigger>
            <TabsTrigger value="wiki">
              <File02Icon fill="violet" size={14} />
              Wiki
            </TabsTrigger>
            <TabsTrigger value="ticketing">
              <Ticket03Icon fill="blue" size={14} />
              Ticketing Data
            </TabsTrigger>
          </TabsList>

          <TabsContent value="snippets">
            <SnippetsTab
              releaseId={draftReleaseId}
              publishedReleaseId={publishedReleaseId}
              simulatorId={simulatorId}
            />
          </TabsContent>

          <TabsContent value="articles">
            <ArticlesTab
              releaseId={draftReleaseId}
              publishedReleaseId={publishedReleaseId}
              simulatorId={simulatorId}
            />
          </TabsContent>

          <TabsContent value="wiki">
            <WikiTab
              releaseId={draftReleaseId}
              publishedReleaseId={publishedReleaseId}
              simulatorId={simulatorId}
            />
          </TabsContent>

          <TabsContent value="ticketing">
            <Flex direction="column" gap="2" py="4" px="2" style={{ position: "relative" }}>
              <Badge color="green" style={{ position: "absolute", top: "16px", right: "0" }}>
                Enabled
              </Badge>
              <Text size="3" weight="medium">
                Ticketing Data
              </Text>
              <Flex direction="column" gap="1">
                <Text size="2" color="gray">
                  Allow the agent to access your ticketing data.
                </Text>
                <Text size="2" color="gray">
                  To change this configuration, please contact{" "}
                  <Link href="mailto:support@quack.ai">support@quack.ai.</Link>
                </Text>
              </Flex>
            </Flex>
          </TabsContent>
        </Tabs>
      </Flex>
    </ConfigurationViewGuard>
  );
}
